import { useEffect, useContext } from "react";

import MedleySizeOptions from "@components/store/product-detail/shared/medley-size-options";
import PropertiesAccordionList from "@components/store/product-detail/shared/properties-accordion-list";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import ProductNotAvailableMessage from "@components/store/product-detail/shared/product-not-available-message";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";
import { getProductType, getProductCategory } from "@helpers/products/types";

import { useProductInfoContext } from "@contexts/product-info-context";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";
import useGetCartAddedVariant from "@hooks/product-detail/use-get-cart-added-variant";

import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";

import styles from "./main.module.scss";

const MedleyDetails = ({
  product,
  masterVariant,
  productQuantity,
  setProductQuantity,
  variantSelected,
  setSelectedVariant,
  medleyProductOptions,
  canBuyProduct,
  renderProductDetailHeader,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { name, dietaryRestrictions, ingredients, vendorByCountry, askForPreference, intent } =
    product;

  const {
    hideVariantSelectionSection,
    showAddToBox,
    isViewOnlyMode,
    renderCatalogModeFooterLinks,
    onSuccessCallback,
    renderCustomVariantSelectionSection,
  } = useProductInfoContext();

  const { convertPriceInStoreCurrency } = useStoreCurrencyConverter();

  const cartAddedVariant = useGetCartAddedVariant({ intent });

  const { renderRestrictionsModal, renderAddToBagBtn, renderMedlyDietryRestrictionsModal } =
    useShowPrefrenceRestrictionModal({
      product,
      selectedVariant: variantSelected,
      isBYOProduct: showAddToBox,
    });

  const handleQuantity = (newQuantity) => {
    setProductQuantity(newQuantity);
  };

  const renderQuantitySelector = () => (
    <div className={styles.qtyColLeft}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={productQuantity}
        setQuantity={setProductQuantity}
        className="defaultStyle"
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    </div>
  );
  const setMedleyOption = (data) => {
    setSelectedVariant(data);
  };

  //set default medley variant
  useEffect(() => {
    let defaultIndex = -1;
    if (cartAddedVariant) {
      const { price: cartPrice, currency: cartCurrency } = cartAddedVariant;
      defaultIndex = medleyProductOptions.findIndex(
        ({ amount }) =>
          Number.parseFloat(amount) ===
          Number.parseFloat(convertPriceInStoreCurrency(cartPrice, cartCurrency)), //This has been added because cart item is in order currency and comparison here is happening in store currency.
      );
      setProductQuantity(cartAddedVariant.quantity);
    } else if (medleyProductOptions.length === 1) defaultIndex = 0;
    defaultIndex > -1 && setMedleyOption(medleyProductOptions[defaultIndex]);
  }, [medleyProductOptions, cartAddedVariant]);

  const updatedProduct = { ...product, priceIn: variantSelected.amount };
  const updatedMasterVariant = { ...masterVariant, priceIn: variantSelected.amount };

  const addToBagProps = {
    product: updatedProduct,
    quantity: productQuantity,
    variant: updatedMasterVariant,
    btnParentClass: styles.bagBtnBlock,
    showAddToBox,
    onSuccessCallback,
    handleQuantity,
    isDisabled: !canBuyProduct,
  };

  return (
    <div className={`${styles.boxContainerFluid} boxContainerFluid`}>
      {!isMobileDevice && renderProductDetailHeader()}

      <VendorHolidayMessage vendor={vendorByCountry} />
      {!hideVariantSelectionSection && (
        <div className={`${styles.dropwithError} ${styles.mainDropdownDiv}`}>
          <MedleySizeOptions
            title="Box Size"
            options={medleyProductOptions}
            selectedData={variantSelected}
            handleChange={setMedleyOption}
            productId={product.id}
            product={product}
            disabled={!canBuyProduct}
          />
        </div>
      )}
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />
      {!(isViewOnlyMode || hideVariantSelectionSection) && (
        <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
          {renderQuantitySelector()}
          {renderAddToBagBtn(addToBagProps)}
        </div>
      )}
      {hideVariantSelectionSection && renderCustomVariantSelectionSection()}
      <ProductDescriptionAccordion product={product} />
      <PropertiesAccordionList
        restrictions={dietaryRestrictions}
        ingredients={ingredients}
        instructions={product?.productDetail?.orderInstructions}
        productId={product.id}
      />
      <ProductInfoSection product={product} />
      {!hideVariantSelectionSection &&
        renderMedlyDietryRestrictionsModal({
          product: updatedProduct,
          quantity: productQuantity,
          variant: updatedMasterVariant,
        })}
      {!hideVariantSelectionSection &&
        askForPreference &&
        renderRestrictionsModal({
          product: updatedProduct,
          quantity: productQuantity,
          variant: variantSelected,
        })}
      {renderCatalogModeFooterLinks()}
    </div>
  );
};

export default MedleyDetails;
